import React from 'react';
import { TruckIcon, EyeIcon, MessageCircleIcon, CopyIcon } from 'lucide-react';
import { JobMessageButton } from '../../messaging/JobMessageButton';
interface Job {
  id: string;
  jobName: string;
  jobNumber: string;
  location: string;
  foreman: string;
  foremanPhone?: string;
  source: string;
  assignedTrucks: string;
  status: 'in-progress' | 'completed' | 'pending';
  tonnageProgress: React.ReactNode;
  startDate: string;
  estimatedHours?: number;
  truckType?: string;
  material?: string;
}
interface SelfGeneratedJobsProps {
  jobs: Job[];
  onViewDetails?: (jobId: string) => void;
  onMessageForeman?: (jobId: string) => void;
  onCreateSimilarJob?: (jobId: string) => void;
}
export const SelfGeneratedJobs = ({
  jobs,
  onViewDetails,
  onMessageForeman,
  onCreateSimilarJob
}: SelfGeneratedJobsProps) => {
  // Filter only self-generated in-progress jobs
  const selfJobs = jobs.filter(job => job.status === 'in-progress' && job.source === 'Self');
  if (selfJobs.length === 0) {
    return <div className="bg-white rounded-md shadow overflow-hidden mt-4 mb-4" data-id="element-976">
        <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-977">
          <div className="flex items-center gap-2" data-id="element-978">
            <span className="h-5 w-5 rounded-full bg-teal-500 flex items-center justify-center text-white" data-id="element-979">
              <TruckIcon size={12} data-id="element-980" />
            </span>
            <h3 className="font-medium" data-id="element-981">Self-Generated Jobs</h3>
            <span className="text-gray-500 text-sm" data-id="element-982">(0 active jobs)</span>
          </div>
        </div>
        <div className="p-8 text-center" data-id="element-983">
          <TruckIcon size={40} className="mx-auto mb-3 text-gray-400" data-id="element-984" />
          <p className="text-gray-600 font-medium" data-id="element-985">
            No active self-generated jobs
          </p>
          <p className="text-sm text-gray-500 mt-1" data-id="element-986">
            When you create and assign jobs directly, they'll appear here
          </p>
        </div>
      </div>;
  }
  return <div className="bg-white rounded-md shadow overflow-hidden mt-4 mb-4" data-id="element-987">
      <div className="flex items-center justify-between p-4 bg-gray-50" data-id="element-988">
        <div className="flex items-center gap-2" data-id="element-989">
          <span className="h-5 w-5 rounded-full bg-teal-500 flex items-center justify-center text-white" data-id="element-990">
            <TruckIcon size={12} data-id="element-991" />
          </span>
          <h3 className="font-medium" data-id="element-992">Self-Generated Jobs</h3>
          <span className="text-gray-500 text-sm" data-id="element-993">
            ({selfJobs.length} active jobs)
          </span>
        </div>
      </div>
      <div className="p-4" data-id="element-994">
        <table className="min-w-full divide-y divide-gray-200" data-id="element-995">
          <thead className="bg-gray-50" data-id="element-996">
            <tr data-id="element-997">
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-998">
                Job Name
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-999">
                Job #
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1000">
                Location
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1001">
                Foreman
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1002">
                Trucks
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1003">
                Material
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1004">
                Progress
              </th>
              <th scope="col" className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider" data-id="element-1005">
                Actions
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200" data-id="element-1006">
            {selfJobs.map(job => <tr key={job.id} className="hover:bg-gray-50" data-id="element-1007">
                <td className="px-3 py-2 whitespace-nowrap text-sm font-medium text-gray-900" data-id="element-1008">
                  {job.jobName}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1009">
                  {job.jobNumber}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1010">
                  {job.location}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1011">
                  {job.foreman}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1012">
                  {job.assignedTrucks}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1013">
                  <span className="px-2 py-1 text-xs font-medium bg-yellow-100 text-yellow-800 rounded-full" data-id="element-1014">
                    {job.material}
                  </span>
                  <div className="text-xs text-gray-500 mt-1" data-id="element-1015">
                    {job.truckType}
                  </div>
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1016">
                  {job.tonnageProgress}
                </td>
                <td className="px-3 py-2 whitespace-nowrap text-sm text-gray-500" data-id="element-1017">
                  <div className="flex space-x-2" data-id="element-1018">
                    <button className="text-green-600 hover:text-green-800 flex items-center" onClick={() => onCreateSimilarJob?.(job.id)} data-id="element-1019">
                      <CopyIcon size={14} className="mr-1" data-id="element-1020" />
                      Recreate
                    </button>
                    <button className="text-blue-600 hover:text-blue-800" onClick={() => onViewDetails?.(job.id)} data-id="element-1021">
                      View
                    </button>
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="foreman" buttonText="Foreman" className="text-purple-600 hover:text-purple-800" data-id="element-1022" />
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="driver" buttonText="Drivers" className="text-indigo-600 hover:text-indigo-800" data-id="element-1023" />
                    <JobMessageButton jobId={job.id} jobName={job.jobName} recipientRole="trucking" buttonText="Trucking" className="text-orange-600 hover:text-orange-800" data-id="element-1024" />
                  </div>
                </td>
              </tr>)}
          </tbody>
        </table>
      </div>
    </div>;
};